import { CarePhase } from '../../../appModel';
import { appliedHealthCareInflation } from '../../../appliedHealthCareInflation';
import { calcMonthlyCareInflatedValue } from './phaseCarePlanCalculations';
import {
  DAYS_IN_MONTH,
  ONE_ADL_DURATION_MULTIPLIER,
  POLICY_ACTIVATES_AT_ADL_COUNT,
} from './sharedConstants';
import { getFirstContributingPhaseStartYear } from './sharedPolicyConfig';

// Per-row count of "non-2-ADL" days — days where the client has < 2 ADLs
// and so isn't eligible for benefit payment. The ineligible bucket sits
// at the start of the phase. Strategies whose products don't use the
// 2-ADL gate can skip this helper.
export function computeNon2AdlDaysThisRow(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): number {
  const phaseCareMonths = Math.floor(phaseCarePlan.phaseCareMonthsNeeded);
  const clientAdlCount = client.intakeSurvey?.clientAdlReceivedAssistanceCount ?? 0;
  const has2AdlGate =
    phaseCarePlan.carePhase === CarePhase.earlyCare &&
    clientAdlCount < POLICY_ACTIVATES_AT_ADL_COUNT;
  if (!has2AdlGate) return 0;
  // Take the 2-ADL boundary DOWN to a WHOLE number of months so it never lands
  // mid-month. Left fractional, an odd-month early-care phase splits a single
  // projection row into a <2-ADL half and an eligible half (a 17-month phase
  // puts the boundary at 8.5 months → a 15-day slice), muddying that month's
  // care-cost application. Flooring (8.5→8, 8.9→8) keeps every row cleanly
  // all-eligible or all-ineligible, and resolves a partial month in the
  // client's favour — the month it straddles pays rather than being withheld.
  const non2AdlBucketMonths = Math.floor(phaseCareMonths * ONE_ADL_DURATION_MULTIPLIER);
  const non2AdlBucketEnd = non2AdlBucketMonths * DAYS_IN_MONTH;
  const dayStart = monthsIntoPhase * DAYS_IN_MONTH;
  const dayEnd = dayStart + DAYS_IN_MONTH;
  return Math.max(0, Math.min(dayEnd, non2AdlBucketEnd) - Math.max(dayStart, 0));
}

export interface MonthlyCostMetrics {
  nominalCareCost: number;
  effectiveCareCost: number;
  dailyCareCost: number;
  // Years of care-cost inflation applied to this row (the exponent on
  // `(1 + inflationRate)`). Surfaced so the UI can label the inflation
  // breakdown without re-deriving it from session-level terms — a derivation
  // that reads wrong for joint partner rows, whose merged-stream monthIndex
  // and earlier-than-the-session-client onset don't match `terms`.
  careCostInflationYears: number;
  // Calendar year this row's care is projected to occur in (claimant-relative).
  projectedCareYear: number;
}

// Shared care-cost inflation clock for one row: whole years of inflation
// applied `monthsIntoPhase` months into `carePhase`, counted from the Stage-1
// anchor (P1-7: the threaded `client.costInflationAnchorYear`, defaulting to
// the current year) along the projection's rounded-month clock
// (`Math.floor(phaseCareMonthsNeeded)` for each prior phase plus
// `monthsIntoPhase`). The earlier per-phase formulation mixed an unrounded
// phase start year with a rounded `monthsIntoPhase`, so each phase boundary
// leaked up to ±½ month into the inflation count.
//
// Extracted so the Stage-1 phase-cost calc can accrue the SAME per-month
// factors the ledger charges (`computeMonthBasedInflatedPhaseCost` below) —
// the two layers agree by construction, not by parallel arithmetic.
function computeRowInflationClock(
  client: Client,
  carePhase: CarePhase,
  monthsIntoPhase: number,
): { yearsFromToday: number; projectedCareYear: number } {
  const currentYear = client.costInflationAnchorYear ?? new Date().getFullYear();
  const firstPhaseStartYear = getFirstContributingPhaseStartYear(client);

  const phasePlans = client.projectionInputs?.phaseCarePlans ?? [];
  const phaseIdx = phasePlans.findIndex(p => p.carePhase === carePhase);
  const monthsBeforeThisPhase = phasePlans
    .slice(0, Math.max(phaseIdx, 0))
    .reduce((sum, p) => sum + Math.floor(p.phaseCareMonthsNeeded), 0);
  const monthIndexWithinClient = monthsBeforeThisPhase + monthsIntoPhase;

  const yearsFromTodayContinuous =
    (firstPhaseStartYear - currentYear) + monthIndexWithinClient / 12;
  return {
    yearsFromToday: Math.max(Math.floor(yearsFromTodayContinuous), 0),
    projectedCareYear: Math.floor(firstPhaseStartYear + monthIndexWithinClient / 12),
  };
}

// Per-month effective care cost. Inflates the phase's nominal
// professional-share cost from "today" to the row's calendar position on the
// shared row clock above. Annual-stepped: a row's cost holds flat across the
// 12 months following each integer-year anniversary. Strategies that need a
// different cost model can reimplement.
export function computeMonthlyCostMetrics(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): MonthlyCostMetrics {
  const phaseCosts = client.phaseCosts?.find(p => p.carePhase === phaseCarePlan.carePhase);
  const nominalPhaseCost = phaseCosts?.phaseProfessionalShareCost ?? 0;
  const phaseMonths = Math.max(Math.floor(phaseCarePlan.phaseCareMonthsNeeded), 1);
  const nominalCareCost = nominalPhaseCost / phaseMonths;
  const inflationRate = appliedHealthCareInflation(client);

  const { yearsFromToday, projectedCareYear } = computeRowInflationClock(
    client,
    phaseCarePlan.carePhase,
    monthsIntoPhase,
  );

  const effectiveCareCost = calcMonthlyCareInflatedValue(
    nominalCareCost,
    inflationRate,
    yearsFromToday * 12,
  );
  return {
    nominalCareCost,
    effectiveCareCost,
    dailyCareCost: effectiveCareCost / DAYS_IN_MONTH,
    careCostInflationYears: yearsFromToday,
    projectedCareYear,
  };
}

// Per-month out-of-pocket room & board for a row. Independent Living is priced
// as home-style care (covered, via `computeMonthlyCostMetrics`) plus room &
// board (never reimbursed). This inflates the phase's nominal room & board to
// the row's calendar position on the SAME clock as the care cost, so the two
// agree by construction. Zero for every non-IL phase — their
// `phaseRoomAndBoardCost` is 0, and the guard short-circuits.
// Returns the nominal (today's-dollar) monthly figure alongside the inflated
// one, so the Care cost tooltip can show room & board's derivation the same way
// it shows care's — `nominal × (1 + rate)^years` — instead of presenting the
// inflated number with no provenance.
export function computeMonthlyRoomAndBoardCost(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): { nominal: number; effective: number } {
  const phaseCosts = client.phaseCosts?.find(p => p.carePhase === phaseCarePlan.carePhase);
  const nominalPhaseRoomAndBoard = phaseCosts?.phaseRoomAndBoardCost ?? 0;
  if (nominalPhaseRoomAndBoard === 0) return { nominal: 0, effective: 0 };

  const phaseMonths = Math.max(Math.floor(phaseCarePlan.phaseCareMonthsNeeded), 1);
  const nominalMonthly = nominalPhaseRoomAndBoard / phaseMonths;
  const inflationRate = appliedHealthCareInflation(client);
  const { yearsFromToday } = computeRowInflationClock(
    client,
    phaseCarePlan.carePhase,
    monthsIntoPhase,
  );
  return {
    nominal: nominalMonthly,
    effective: calcMonthlyCareInflatedValue(nominalMonthly, inflationRate, yearsFromToday * 12),
  };
}

// Month-based per-phase inflated cost (Stage 1, claim-projection-engine gate).
// Accrues `nominalPhaseCost` month by month on the ledger's clock: each of the
// phase's floored months carries `nominal / months` inflated by that row's
// whole-year factor — for the professional share, term-for-term what
// `computeMonthlyCostMetrics` charges the engine per row. Summed per phase,
// the Stage-1 `phaseInflated*` totals therefore equal the ledger's
// Σ effectiveCareCost, restoring the 100%-coverage identity (an uncapped
// reimbursement policy's payout equals the app-wide projected cost). The
// closed form it replaces inflates the WHOLE phase cost to the phase-END
// year — systematically higher for any future phase. Generalized over the
// nominal input so the family-care and no-discount variants inflate on the
// identical clock.
//
// A phase whose plan rounds to 0 months has no ledger rows; any cost it
// carries is returned uninflated — mirroring the past-phase branch of
// `calcInflatedCarePhaseCosts` rather than vanishing from the totals.
export function computeMonthBasedInflatedPhaseCost(
  client: Client,
  carePhase: CarePhase,
  nominalPhaseCost: number,
): number {
  const phasePlans = client.projectionInputs?.phaseCarePlans ?? [];
  const plan = phasePlans.find(p => p.carePhase === carePhase);
  const phaseMonths = plan ? Math.floor(plan.phaseCareMonthsNeeded) : 0;
  if (phaseMonths <= 0) return nominalPhaseCost;

  const nominalMonthly = nominalPhaseCost / phaseMonths;
  const inflationRate = appliedHealthCareInflation(client);
  let total = 0;
  for (let monthsIntoPhase = 0; monthsIntoPhase < phaseMonths; monthsIntoPhase++) {
    const { yearsFromToday } = computeRowInflationClock(client, carePhase, monthsIntoPhase);
    total += calcMonthlyCareInflatedValue(nominalMonthly, inflationRate, yearsFromToday * 12);
  }
  return total;
}
